import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Wordmark, Rule, Eyebrow } from "@/components/editorial";
import { organizationSchema, breadcrumbSchema } from "@/lib/seo/schemas";

export function AboutPage() {
  return (
    <div className="min-h-full bg-background font-sans text-foreground">
      <Seo
        title="À propos"
        description="Batch Cooking est une application parisienne de planification de repas hebdomadaire. Notre mission : rendre le batch cooking accessible à tous, intelligemment."
        canonicalPath="/about"
        jsonLd={[
          organizationSchema,
          breadcrumbSchema([
            { name: "Accueil", path: "/" },
            { name: "À propos", path: "/about" },
          ]),
        ]}
      />

      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-hair bg-background/95 px-5 py-5 backdrop-blur lg:px-[72px]">
        <Wordmark size="md" />
        <Link
          to="/"
          className="flex items-center gap-1.5 text-[13px] text-ink-70 hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" />
          Accueil
        </Link>
      </header>

      <main>
        {/* Hero */}
        <section className="border-b border-hair px-5 py-20 lg:px-[72px] lg:py-28">
          <div className="max-w-[860px]">
            <Eyebrow rule className="mb-8">
              Paris · 2026
            </Eyebrow>
            <h1 className="mb-8 font-serif text-[42px] font-normal leading-tight tracking-[-0.01em] text-foreground lg:text-[56px]">
              Cuisiner moins,
              <br />
              <span className="italic text-accent">manger mieux.</span>
            </h1>
            <p className="max-w-[640px] font-serif text-xl font-light leading-relaxed text-ink-70 lg:text-[22px]">
              Batch Cooking est né d'une conviction simple : la cuisine du
              quotidien ne devrait pas être une source de stress. Un après-midi
              bien organisé suffit à nourrir toute une semaine, sainement.
            </p>
          </div>
        </section>

        {/* Manifeste */}
        <section className="bg-paper px-5 py-20 lg:px-[72px] lg:py-24">
          <div className="grid gap-12 lg:grid-cols-[280px_1fr] lg:gap-20">
            <div>
              <Eyebrow className="mb-4">Manifeste</Eyebrow>
              <h2 className="font-serif text-[30px] font-normal leading-tight text-foreground">
                Notre <span className="italic">approche</span>.
              </h2>
            </div>
            <div className="space-y-6 font-serif text-lg font-light leading-relaxed text-ink-70">
              <p>
                Le batch cooking n'est pas une mode. C'est un geste ancien —
                cuisiner en lot, préparer ses bases, composer au fil de la
                semaine. Ce que nous avons rendu moderne, c'est l'orchestration.
              </p>
              <p>
                Notre IA ne remplace pas votre jugement en cuisine. Elle
                l'amplifie : elle calcule les bases communes, respecte vos
                contraintes nutritionnelles, anticipe vos courses. Vous gardez
                le contrôle — à chaque étape.
              </p>
              <p>
                Nous croyons que bien manger ne devrait pas coûter plus cher ni
                prendre plus de temps. Nos utilisateurs économisent en moyenne{" "}
                <strong className="font-medium text-foreground">
                  54 €/semaine
                </strong>{" "}
                de gaspillage, et réduisent leur temps en cuisine de{" "}
                <strong className="font-medium text-foreground">38 %</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* Valeurs */}
        <section className="px-5 py-20 lg:px-[72px] lg:py-24">
          <Eyebrow className="mb-10">Ce qui nous guide</Eyebrow>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                n: "01",
                t: "Transparence alimentaire",
                b: "Chaque recette affiche ses allergènes, son Nutri-Score estimé et ses durées de conservation. Les limites de l'IA sont clairement indiquées.",
              },
              {
                n: "02",
                t: "Editorial, pas algorithmique",
                b: "L'esthétique de Batch Cooking est inspirée de la presse culinaire — sobre, lisible, sans surcharge informationnelle.",
              },
              {
                n: "03",
                t: "Frugalité computationnelle",
                b: "Les assets de cuisine sont issus d'une bibliothèque statique — nous ne générons pas d'images par IA à chaque recette, pour des raisons écologiques et de coût.",
              },
            ].map((v) => (
              <article key={v.n}>
                <div className="mb-3 font-serif text-lg italic text-accent">
                  {v.n}
                </div>
                <Rule className="mb-4" />
                <h3 className="mb-3 font-serif text-[22px] font-normal leading-snug text-foreground">
                  {v.t}
                </h3>
                <p className="text-[14px] leading-relaxed text-ink-60">{v.b}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="border-t border-hair bg-paper px-5 py-16 lg:px-[72px]">
          <div className="flex flex-wrap items-center justify-between gap-8">
            <div>
              <h2 className="mb-2 font-serif text-[26px] font-normal text-foreground">
                Nous contacter
              </h2>
              <p className="text-[14px] text-ink-60">
                Presse, partenariats, retours produit.
              </p>
              <a
                href="mailto:contact@lapopote.co"
                className="mt-3 inline-block border-b border-foreground pb-0.5 text-[13px] uppercase tracking-[0.08em] text-foreground hover:border-accent hover:text-accent"
              >
                contact@batchcooking.fr
              </a>
            </div>
            <div className="flex gap-4">
              <Button asChild size="lg">
                <Link to="/onboarding">
                  Essayer l'application <ArrowRight />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/faq">FAQ</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
